import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Dialog } from '@material-ui/core';
import {
  CmpMemberInvoiceRowFragment, GetCmpMemberInvoicesDocument, GetCmpMemberInvoicesQuery,
  GetCmpMemberInvoiceDetailDocument, GetCmpMemberInvoiceDetailQuery,
  GetCmpMemberInvoiceDocumentDocument, GetCmpMemberInvoiceDocumentMutation,
  StartCmpMemberInvoiceCheckoutDocument, StartCmpMemberInvoiceCheckoutMutation,
} from '../../../../graphql/types';
import useMemberDashboardAccess from '../../../common/Hooks/useMemberDashboardAccess';
import { INVOICES_ONLY_LOGIN_NOTE, isInvoicesOnlyMemberAccess, memberInvoiceIdentityKey } from './memberInvoiceAccess';
import { getCreditedByRowLinkLines, getCreditNoteRowLinkLines } from './memberInvoiceCreditLinks';
import './memberInvoices.css';

type Row = CmpMemberInvoiceRowFragment;
type List = GetCmpMemberInvoicesQuery['getCmpMemberInvoices'];
type DocumentView = { url: string; filename: string };
const money = (amount: number, currency: string) => new Intl.NumberFormat('en-CA', {
  style: 'currency', currency,
}).format(amount / 100);
const labels: Record<string, string> = {
  AWAITING_PAYMENT: 'Awaiting Payment', OVERDUE: 'Overdue', PAID: 'Paid',
  CREDITED: 'Credited', REFUNDED: 'Refunded', VOIDED: 'Voided',
  WAIVED: 'Waived', NOT_PAID: 'Not Paid',
};
// A credit note names the invoice it credits and a source invoice names its
// credit notes (Oct 8 2026). Older rows without the links keep their note.
const statusNote = (row: Row): string => row.creditForInvoiceNumber
  ? 'Refunded'
  : row.installmentSummary || (row.daysPastDue ? `${row.daysPastDue} Days Overdue` : row.paymentMethodLabel !== 'Not paid' && row.paymentMethodLabel !== '-' ? row.paymentMethodLabel : '');
export const memberInvoiceCheckoutUrl = (value: string): string => {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com' || url.port || url.username || url.password) {
    throw new Error('Payment destination is unavailable.');
  }
  return url.href;
};
const operationId = () => Array.from(crypto.getRandomValues(new Uint8Array(16)))
  .map(value => value.toString(16).padStart(2, '0')).join('');

export type MemberInvoicesViewProps = {
  chamberName: string; timeZone?: string; data?: List; loading: boolean; error?: string;
  invoicesOnly?: boolean;
  offset: number;
  onPage: (offset: number) => void; onRetry: () => void;
  onOpen: (row: Row, mode: 'document' | 'payment') => void;
  selected?: Row; mode?: 'document' | 'payment'; document?: DocumentView;
  busy: boolean; actionError?: string; onClose: () => void;
  onPay: () => void; onDocumentRetry: () => void; onPaymentOpen: () => void;
};

/** The real invoice surface. Data and actions are supplied by the authenticated container. */
export function MemberInvoicesView(props: MemberInvoicesViewProps) {
  const [zoom, setZoom] = useState('Fit on page');
  useEffect(() => { setZoom('Fit on page'); }, [props.selected?.id]);
  const outstanding = props.data?.outstanding || [];
  return <main className="member-invoices">
    <header className="mi-heading"><h1>Invoices &amp; Payments</h1>
      <p>View invoices issued by {props.chamberName} and pay any outstanding balance.</p>
      {props.invoicesOnly && <p className="mi-login-note">{INVOICES_ONLY_LOGIN_NOTE}</p>}</header>
    <div className="mi-card"><section className="mi-section" aria-label="Invoices">
      <h2>Invoices</h2>
      {props.loading ? <p role="status">Loading invoices…</p> : props.error ?
        <div role="alert"><p>{props.error}</p><button className="mi-button" onClick={props.onRetry}>Try again</button></div> : <>
        {!props.data?.totalCount ? <p>No invoices have been issued to your account.</p> :
          outstanding.length ? <p className="mi-summary">You have <b>{outstanding.reduce((sum, item) => sum + item.count, 0)}</b> outstanding invoice(s) totalling <b>{outstanding.map(item => money(item.amountInCents, item.currency)).join(' + ')}</b>.</p> :
          <p className="mi-settled">No outstanding balance.</p>}
        {!!props.data?.rows.length && <div className="mi-table-scroll" tabIndex={0} aria-label="Invoice table, scroll horizontally for all columns">
          <table><thead><tr>{['Invoice #', 'Date', 'Description', 'Amount', 'Status', 'Action'].map(label => <th key={label}>{label}</th>)}</tr></thead>
            <tbody>{props.data.rows.map(row => <tr key={row.id}>
              <td><button className="mi-invoice-link" onClick={() => props.onOpen(row, 'document')}>{row.invoiceNumber}</button></td>
              <td>{new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', year: 'numeric', timeZone: props.timeZone || 'UTC' }).format(row.issuedAtUtcMilli)}</td>
              <td>{row.description}</td><td className="mi-amount">{money(row.totalAmountInCents, row.currency)}</td>
              <td><strong className={`mi-status mi-status-${row.displayState.toLowerCase()}`}>{labels[row.displayState] || row.displayState.replace(/_/g, ' ')}</strong>
                <span className="mi-status-note">{statusNote(row)}</span>
                {[...getCreditNoteRowLinkLines(row.creditForInvoiceNumber), ...getCreditedByRowLinkLines(row.creditedByInvoiceNumbers)]
                  .map(line => <span key={line} className="mi-status-note mi-status-link" title={line}>{line}</span>)}</td>
              <td>{row.canPay ? <button className="mi-button" onClick={() => props.onOpen(row, 'payment')}>Pay Now</button> : <span title={row.paymentUnavailableReason || undefined}>—</span>}</td>
            </tr>)}</tbody></table>
        </div>}
        {!!props.data && (props.data.totalCount > 50 || props.offset > 0) && <nav className="mi-pagination" aria-label="Invoice pages">
          <button disabled={!props.offset} onClick={() => props.onPage(Math.max(0, props.offset - 50))}>Previous</button>
          <span>{props.offset + 1}–{Math.min(props.offset + 50, props.data.totalCount)} of {props.data.totalCount}</span>
          <button disabled={props.offset + 50 >= props.data.totalCount} onClick={() => props.onPage(props.offset + 50)}>Next</button>
        </nav>}
      </>}
      <p className="mi-data-note">Invoice data is synced from {props.chamberName}'s Chamber Member Pro™ dashboard.</p>
    </section></div>
    <Dialog open={!!props.mode} onClose={props.busy ? undefined : props.onClose} fullWidth maxWidth={props.mode === 'document' ? false : 'sm'}
      fullScreen={props.mode === 'document'} aria-labelledby="mi-dialog-title" PaperProps={{ className: `mi-dialog mi-${props.mode}` }}>
      {props.mode === 'payment' ? <div className="mi-payment-content">
        <div className="mi-dialog-heading"><h2 id="mi-dialog-title">Process Payment</h2><button aria-label="Close payment" disabled={props.busy} onClick={props.onClose}>×</button></div>
        <dl className="mi-payment-context"><div><dt>Bill To</dt><dd>{props.selected?.billToName}</dd></div>
          <div><dt>Reference Invoice</dt><dd>{props.selected?.invoiceNumber}</dd></div>
          <div><dt>Balance Owing</dt><dd>{props.selected && money(props.selected.amountDueInCents, props.selected.currency)}</dd></div></dl>
        <div className="mi-payment-amount"><span>AMOUNT TO PAY</span><strong>{props.selected && money(props.selected.payableAmountInCents, props.selected.currency)}</strong>
          {props.selected?.installmentSummary && <small>{props.selected.installmentSummary}</small>}</div>
        <div className="mi-method"><span>Credit Card</span><strong>Selected</strong></div>
        <p>You will enter your card details on Stripe's secure payment page. This invoice updates after payment is confirmed.</p>
        {props.actionError && <p role="alert">{props.actionError}</p>}
        <div className="mi-payment-actions"><button className="mi-pay" disabled={props.busy || !props.selected?.canPay} onClick={props.onPay}>{props.busy ? 'Opening secure payment…' : 'Continue to Secure Payment'}</button>
          <button disabled={props.busy} onClick={props.onClose}>Cancel</button></div>
      </div> : <>
        <div className="mi-document-toolbar"><h2 id="mi-dialog-title" className="mi-sr-only">Invoice {props.selected?.invoiceNumber}</h2>
          <div className="mi-zoom" aria-label="Document zoom">{['25%', '50%', '75%', '100%', 'Fit on page'].map(value => <button key={value} aria-pressed={zoom === value} onClick={() => setZoom(value)}>{value}</button>)}</div>
          <div className="mi-document-actions">{props.selected?.canPay && <button className="mi-pay" disabled={props.busy} onClick={props.onPaymentOpen}>Add Payment</button>}
            {props.document && <a className="mi-print" href={props.document.url} target="_blank" rel="noopener noreferrer"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="7"/></svg>Print PDF</a>}
            <button onClick={props.onClose} aria-label="Close document">×</button></div>
        </div>
        {props.busy && <p role="status">Loading invoice document…</p>}
        {props.actionError && <div role="alert"><p>{props.actionError}</p><button onClick={props.onDocumentRetry}>Try again</button></div>}
        {props.document && <iframe key={`${props.document.url}-${zoom}`} title={`Invoice ${props.selected?.invoiceNumber} document`}
          className="mi-pdf" src={`${props.document.url}#toolbar=0&navpanes=0&${zoom === 'Fit on page' ? 'view=Fit' : `zoom=${zoom.replace('%', '')}`}`} />}
      </>}
    </Dialog>
  </main>;
}

export default function MemberInvoices() {
  const client = useApolloClient();
  const { user, access, error: accessError, refetch } = useMemberDashboardAccess();
  const identity = user && access?.canViewInvoices && access.cmpEnabled
    ? memberInvoiceIdentityKey(user.id, user.chamberId, user.chamberMemberId || '', access.contactId) : '';
  const [data, setData] = useState<List>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [offset, setOffset] = useState(0);
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<Row>();
  const [mode, setMode] = useState<'document' | 'payment'>();
  const [document, setDocument] = useState<DocumentView>();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const epoch = useRef(0);
  const paymentRequest = useRef<number>();
  const currentIdentity = useRef(identity); currentIdentity.current = identity;
  const operations = useRef(new Map<string, string>());
  const close = useCallback(() => {
    epoch.current += 1; paymentRequest.current = undefined; setMode(undefined); setSelected(undefined); setDocument(undefined); setBusy(false); setActionError(undefined);
  }, []);
  useEffect(() => () => { if (document) URL.revokeObjectURL(document.url); }, [document]);
  useEffect(() => {
    const request = ++epoch.current;
    setData(undefined); setError(undefined); setLoading(true); setMode(undefined); setSelected(undefined); setDocument(undefined); setBusy(false);
    if (!identity) { setLoading(false); return; }
    client.query<GetCmpMemberInvoicesQuery>({ query: GetCmpMemberInvoicesDocument,
      variables: { input: { limit: 50, offset } }, fetchPolicy: 'no-cache' })
      .then(result => { if (request === epoch.current && currentIdentity.current === identity) setData(result.data.getCmpMemberInvoices); })
      .catch(() => { if (request === epoch.current) setError('Invoices could not be loaded. Please try again.'); })
      .finally(() => { if (request === epoch.current) setLoading(false); });
    return () => { epoch.current += 1; };
  }, [client, identity, offset, revision]);
  useEffect(() => { operations.current.clear(); paymentRequest.current = undefined; setOffset(0); }, [identity]);
  useEffect(() => {
    if (!identity) return;
    const refresh = () => { refetch().then(() => setRevision(value => value + 1)).catch(close); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refetch, close, identity]);
  const open = async (row: Row, nextMode: 'document' | 'payment') => {
    const request = ++epoch.current;
    setSelected(row); setMode(nextMode); setBusy(true); setActionError(undefined); setDocument(undefined);
    try {
      const detail = await client.query<GetCmpMemberInvoiceDetailQuery>({ query: GetCmpMemberInvoiceDetailDocument,
        variables: { input: { invoiceId: row.id } }, fetchPolicy: 'no-cache' });
      if (request !== epoch.current || currentIdentity.current !== identity) return;
      setSelected(detail.data.getCmpMemberInvoiceDetail);
      if (nextMode === 'document') {
        const response = await client.mutate<GetCmpMemberInvoiceDocumentMutation>({ mutation: GetCmpMemberInvoiceDocumentDocument,
          variables: { input: { invoiceId: row.id } }, fetchPolicy: 'no-cache' });
        if (request !== epoch.current || currentIdentity.current !== identity) return;
        const file = response.data?.getCmpMemberInvoiceDocument;
        if (!file || file.contentType !== 'application/pdf') throw new Error('Invalid document');
        const bytes = Uint8Array.from(atob(file.base64Data), char => char.charCodeAt(0));
        setDocument({ url: URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })), filename: file.filename });
      } else if (!detail.data.getCmpMemberInvoiceDetail.canPay) throw new Error('Payment unavailable');
    } catch {
      if (request === epoch.current) { setData(undefined); setError('Invoices are unavailable. Refresh the list to check access.'); setSelected(undefined); setActionError('This invoice is unavailable or your access has changed. Close this window and refresh the list.'); }
    } finally { if (request === epoch.current) setBusy(false); }
  };
  const pay = async () => {
    if (!selected?.canPay || busy || paymentRequest.current !== undefined || !identity) return;
    const popup = window.open('about:blank', '_blank');
    if (!popup) { setActionError('Allow a new tab for secure payment, then try again.'); return; }
    popup.opener = null;
    const request = ++epoch.current; paymentRequest.current = request; setBusy(true); setActionError(undefined);
    try {
      const id = operations.current.get(selected.id) || operationId(); operations.current.set(selected.id, id);
      const response = await client.mutate<StartCmpMemberInvoiceCheckoutMutation>({ mutation: StartCmpMemberInvoiceCheckoutDocument,
        variables: { input: { invoiceId: selected.id, operationId: id } }, fetchPolicy: 'no-cache' });
      if (request !== epoch.current || currentIdentity.current !== identity) { popup.close(); return; }
      popup.location.href = memberInvoiceCheckoutUrl(response.data?.startCmpMemberInvoiceCheckout.checkoutUrl || '');
      close();
    } catch {
      popup.close();
      if (request === epoch.current) { setData(undefined); setError('Refresh the invoice list before another payment attempt.'); setSelected(undefined); setActionError('Secure payment could not be opened. Refresh this invoice and try again.'); }
    } finally { if (paymentRequest.current === request) paymentRequest.current = undefined; if (request === epoch.current) setBusy(false); }
  };
  if (!identity) return <main className="member-invoices"><div className="mi-card" role={accessError || access ? 'alert' : 'status'}>
    {accessError || access ? 'Invoices are not available for your account.' : 'Checking invoice access…'}</div></main>;
  return <MemberInvoicesView chamberName={user?.chamberInfo.name || ''} timeZone={user?.chamberMemberProTimeZone || 'UTC'} data={data} loading={loading} error={error}
    invoicesOnly={isInvoicesOnlyMemberAccess(access)} offset={offset}
    onPage={setOffset} onRetry={() => setRevision(value => value + 1)} onOpen={open} selected={selected} mode={mode}
    document={document} busy={busy} actionError={actionError} onClose={close} onPay={pay}
    onDocumentRetry={() => selected ? open(selected, 'document') : close()}
    onPaymentOpen={() => selected && open(selected, 'payment')} />;
}
